import React, { useCallback } from 'react';
import TimelineGeneric from '../TimelineGeneric/TimelineGeneric';

import { Block, Flex, Icon, Link, Text } from '@upsales/components';
import type { TimelineCardComponent } from '../TimelineGeneric/TimelineCardConfigs';
import { useTranslation } from 'Components/Helpers/translate';
import BouncyThumbtack from 'App/components/Icon/BouncyThumbtack';
import Signals from 'Resources/Signals';

import './TimelineCardNews.scss';
import BemClass from '@upsales/components/Utils/bemClass';

const mediumIconMap = {
	2: 'cision',
	3: 'di',
	4: 'breakit',
	9: 'borskollen',
	10: 'avanza',
	11: 'economist',
	12: 'idg'
} as const;

const NewsMediumIcon = ({ mediumId }: { mediumId: number | undefined }) => {
	return <Icon name={(mediumIconMap as MaybeObj<typeof mediumIconMap>)[mediumId ?? -1] ?? 'newspaper'} />;
};

const addToEvents = function (event: { news?: { id: number; date: string } | null; clientId?: number }) {
	if (event.news?.id && event.clientId) {
		return Signals.pin(event.clientId, event.news.id, event.news?.date);
	}
};

const removeFromEvents = function (event: { id?: number; eventId?: number }) {
	const id = event.eventId ?? event.id;
	if (id) {
		return Signals.unpin(id);
	}
};

const TimelineCardNews = (({ event, renderedByType, renderedByModal, ...passthrough }) => {
	const { t } = useTranslation();
	const { news } = event || {};

	const openArticle = useCallback(() => {
		window.open(news?.link, '_blank');
	}, []);

	if (!news) {
		return null;
	}

	const isPinned = news.pinned || event.subType === 'Pinned';
	const classes = new BemClass('TimelineCardNews');

	return (
		<TimelineGeneric
			event={event}
			actionTitle={''}
			titleElements={[
				() => (
					<Flex direction="column">
						<Block space="pts pbm">
							<Text bold>{news.title}</Text>
						</Block>
						<Block space="pbs" className={classes.elem('content').b()}>
							<Text>{news.content}</Text>
						</Block>
					</Flex>
				)
			]}
			subTitleElements={[
				'time',
				() => (
					<Text color="grey-10">
						{'• '}
						<NewsMediumIcon mediumId={news.medium} /> {news.name}
						{'\u00A0\u00A0'}
						<Link href={news.link} target={'_blank'}>
							{t('news.readArticle')}
						</Link>
					</Text>
				)
			]}
			actions={
				<Flex className={classes.elem('actions').b()}>
					{!renderedByModal && news.img ? (
						<img
							className={classes.elem('thumbnail').b()}
							src={news.img}
							height={72}
							onClick={openArticle}
							onError={i => {
								(i.target as HTMLImageElement).src = '';
							}}
						/>
					) : null}
					<BouncyThumbtack
						pinned={isPinned}
						onToggle={async value => {
							if (value) {
								addToEvents(event);
							} else {
								removeFromEvents(event);
							}
						}}
						pinnedTooltip={t('prospecting.news.removeFromTimeline')}
						unPinnedTooltip={t('prospecting.news.addToTimeline')}
						tooltipDistance={20}
						size="lg"
					/>
				</Flex>
			}
			onCardClick={openArticle}
			includeIcon
			contactPreposition={''}
			{...passthrough}
			hasAvatar={false}
		/>
	);
}) satisfies TimelineCardComponent;

export default TimelineCardNews;
